import { Component, Signal, computed, effect } from '@angular/core';

import {Scenario, getScenarioKey} from '@scenario/models/scenario.model';
import { UnitRate } from '@scenario/models/unit-rate.model';
import { excerpt } from '@utils/excerpt';
import { InsightService } from '@app/services/insight.service';
import { select } from '@store/injectables';
import { selectSelectedScenarios } from '@store/scenario/scenarios/selected-scenarios-slice';
import {
  selectSelectedNominalUnitRate,
  selectSelectedUnitRates,
} from '@store/scenario/unit-rates/selected-unit-rates-slice';
import { MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatNoDataRow, MatRow, MatRowDef, MatTable } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { DecimalPipe } from '@angular/common';
import { HistoricalDataMode } from '@scenario/enums/historical-data-mode.enum';
import { selectHistoricalDataMode } from '@store/scenario/app/app-slice';
import { applyHistoricalDataMode } from '@scenario/models/historical-data-mode.utils';

// scenario
@Component({
    selector: 'app-scenarios-price-list',
    templateUrl: './scenarios-price-list.component.html',
    styleUrl: './scenarios-price-list.component.scss',
    imports: [MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatTooltip, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, MatNoDataRow, DecimalPipe]
})
export class ScenariosPriceListComponent {
  selectedScenariosSignal: Signal<Scenario[]> = select(selectSelectedScenarios);
  selectedUnitRatesSignal: Signal<UnitRate> = select(selectSelectedUnitRates);
  selectedNominalUnitRateSignal: Signal<UnitRate> = select(selectSelectedNominalUnitRate);
  historicalDataModeSignal: Signal<HistoricalDataMode> = select(selectHistoricalDataMode);

  displayedScenarios = computed(() =>
    applyHistoricalDataMode(this.selectedScenariosSignal(), this.historicalDataModeSignal())
  );

  years = computed(() => generateYearsToDisplay(this.displayedScenarios()));
  displayedColumns = computed(() => ['Scenario ID', ...this.years()]);

  constructor(private readonly insightService: InsightService) {
    effect(() => {
      const scenarios = this.displayedScenarios();
      this.insightService.recordEvent('tabledisplay', { selectedScenariosCount: scenarios.length });
    });
  }

  getScenarioKey = getScenarioKey;
  excerpt = excerpt;

  historicalRate(year: string): number {
    switch (this.historicalDataModeSignal()) {
      case HistoricalDataMode.FORECAST:
        return this.selectedUnitRatesSignal().ratesByYear[year] || 1;

      case HistoricalDataMode.HISTORICAL:
        return this.selectedUnitRatesSignal().ratesByYear[year] || 1;

      case HistoricalDataMode.COMBINED:
        return this.selectedNominalUnitRateSignal().ratesByYear[year] || 1;

      default:
        return this.selectedUnitRatesSignal().ratesByYear[year] || 1;
    }
  }

  forecastRate(year: string): number {
    return this.selectedUnitRatesSignal().ratesByYear[year] || 1;
  }

}

const generateYearsToDisplay = (scenarios: Scenario[]): string[] => {
  const years: number[] = [];
  const currentYear = new Date().getFullYear();

  let minYear = currentYear - 2;
  scenarios.forEach(scenario => {
    const historicalYears = Object.keys(scenario.historicalPricePerYear).map(Number);
    if (historicalYears.length > 0) {
      minYear = Math.min(minYear, ...historicalYears);
    }
  });
  for (let year = minYear; year <= currentYear + 6; year++) {
    years.push(year);
  }

  const startOfFiveYears = Math.ceil((currentYear + 1) / 5) * 5 + 5;
  const endOfFiveYears = startOfFiveYears + 30;

  for (let year = startOfFiveYears; year <= endOfFiveYears; year += 5) {
    if (!years.includes(year)) {
      years.push(year);
    }
  }

  return years.map(year => year.toString());
};
